// components/AgendaPage.tsx
import React, { useState, useEffect, useCallback } from "react";
import MiniCalendar from "./MiniCalendar";
import CalendarView from "./CalendarView";
import Loader from "./Loader";
import api from "../api.js";
import "../style/css/Calendar.css";
import {
  CalendarDateState,
  Appointment,
  CalendarView as ViewType,
} from "../interfaces/types";

const AgendaPage: React.FC = () => {
  const [calendarState, setCalendarState] = useState<CalendarDateState>({
    selectedDate: new Date(),
    currentMonth: new Date(),
    currentView: "month",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);

  // Mock data - replace with actual API calls
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setIsLoading(true);

        // Use UTC dates for API calls to avoid timezone issues
        const startDate = getStartOfMonthUTC(calendarState.currentMonth);
        const endDate = getEndOfMonthUTC(calendarState.currentMonth);
        const browserTimeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startISO = startDate.toISOString(); // Ej: 2025-10-31T06:00:00.000Z
        const endISO = endDate.toISOString(); // Ej: 2025-11-01T06:00:00.000Z
        // Await the API call and get the response data
        const response = await api.get(
          `/appointments/?start=${startISO}&end=${endISO}&timeZone=${browserTimeZone}`,
        );

        // Transform backend data to match your frontend structure
        // inside fetchAppointments() — replace the transform block with this
        const transformedAppointments = response.data.results.map(
          (apt: any) => {
            // parse start/end (ISO with offset), will produce a valid Date if backend returns ISO
            const start = apt.start_time ? new Date(apt.start_time) : null;
            const end = apt.end_time ? new Date(apt.end_time) : null;

            // compute duration in minutes if we have both times
            let duration = "";
            if (
              start &&
              end &&
              !isNaN(start.getTime()) &&
              !isNaN(end.getTime())
            ) {
              const diffMs = end.getTime() - start.getTime();
              const mins = Math.round(diffMs / 60000);
              duration = `${mins} min`;
            } else if (apt.duration_minutes) {
              duration = `${apt.duration_minutes} min`;
            } else if (apt.duration) {
              duration = apt.duration;
            }

            // pick patient name (response has patient.name)
            const patientName =
              (apt.patient && (apt.patient.name || apt.patient.full_name)) ||
              apt.patient_name ||
              apt.patientName ||
              apt.title;

            // title/service fallback
            const service =
              apt.title || apt.service_type || apt.service || "Appointment";

            // safe date object (use start as the appointment date/time)
            const date = start && !isNaN(start.getTime()) ? start : null;

            return {
              id: apt.id, // keep original id (string uuid is fine)
              patientName,
              service,
              duration,
              date, // may be null if no start_time
              time: date ? formatTimeFromDate(date) : "",
              type: apt.appointment_type || apt.type || null,
              status: apt.status || "unknown",
              raw: apt, // optional: keep raw for debugging
            };
          },
        );

        // log so you can verify structure

        setAppointments(
          transformedAppointments.filter((a: any) => a.date !== null), // only keep items with a valid date
        );

        setAppointments(transformedAppointments);
      } catch (error) {
        // Optionally set some fallback or error state
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();
  }, [calendarState.currentMonth]);

  // Helper functions with UTC timezone handling
  const getStartOfMonthUTC = (date: Date): Date => {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1));
  };

  const getEndOfMonthUTC = (date: Date): Date => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
    );
  };

  // Helper to format time from date object
  const formatTimeFromDate = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Initial load effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Hide loader after fade out animation
      setTimeout(() => setShowLoader(false), 400);
    }, 0); // Show loader for 1.5 seconds on initial load

    return () => clearTimeout(timer);
  }, []);

  // Effect for scroll to top when view or date changes
  useEffect(() => {
    if (!isLoading) {
      scrollToTop();
    }
  }, [
    calendarState.currentView,
    calendarState.selectedDate,
    isLoading,
    scrollToTop,
  ]);

  const handleDateSelect = (date: Date) => {
    setCalendarState((prev) => ({
      ...prev,
      selectedDate: date,
      currentMonth: new Date(date.getFullYear(), date.getMonth(), 1),
    }));
  };

  const handleMonthChange = (newMonth: Date) => {
    setCalendarState((prev) => ({
      ...prev,
      currentMonth: newMonth,
    }));
  };

  const handleViewChange = (view: ViewType) => {
    setCalendarState((prev) => ({
      ...prev,
      currentView: view,
    }));
  };

  const mainContainerClass = `container agenda-content ${
    calendarState.currentView === "month" ? "full-width-main" : ""
  }`;

  // Show loader during initial load
  if (showLoader) {
    return <Loader />;
  }

  return (
    <>
      {isLoading && <Loader />}
      <main className={mainContainerClass} id="main-content-container">
        {calendarState.currentView !== "month" && (
          <MiniCalendar
            selectedDate={calendarState.selectedDate}
            currentMonth={calendarState.currentMonth}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
          />
        )}

        <CalendarView
          currentView={calendarState.currentView}
          selectedDate={calendarState.selectedDate}
          currentMonth={calendarState.currentMonth}
          appointments={appointments}
          onViewChange={handleViewChange}
          onDateSelect={handleDateSelect}
          onMonthChange={handleMonthChange}
        />
      </main>
    </>
  );
};

export default AgendaPage;
